import { firestore, functions } from "@/services/firebase";
import { TournamentManagerFactory } from "vue-chess-shared/tournament/TournamentManagerFactory";

const handleBBPPair = functions.httpsCallable("tournament-handleBBPPair");
const addPlayersToTournamentFunction = functions.httpsCallable(
  "offlineTournament-addPlayersToTournament"
);
const tournamentManager = TournamentManagerFactory.createForOfflineClass(
  firestore,
  async (data) => (await handleBBPPair(data))?.data
);

/**
 * Shared service for class tournament creation and management
 * Eliminates code duplication between LectureControl and OfflineClassTournaments
 */
export class ClassTournamentService {
  /**
   * Create a new class tournament (fast operation - creates document only)
   * Students are added asynchronously via Firebase function
   * @param {Object} options - Configuration options
   * @param {Object} options.tournamentData - Tournament properties (name, timeControl, etc.)
   * @param {Object} options.currentClass - Current class object
   * @param {Array} options.participatingStudentIds - Array of student IDs who should participate (optional)
   * @returns {Promise<string>} - Tournament ID
   */
  static async createTournament({ tournamentData, currentClass }) {
    // Create the tournament document (fast operation)
    const tournamentId = await tournamentManager.createTournament({
      ...tournamentData,
      currentRound: 0,
      classId: currentClass.classId,
      createdAt: new Date(),
    });

    return tournamentId;
  }

  /**
   * Transform student data to player data for tournaments
   * Uses student.studentId directly as username (consistent approach)
   * @param {Object} student - Student object
   * @param {Object} currentClass - Current class object
   * @returns {Object} - Player data for tournament
   */
  static transformStudentToPlayer(student, currentClass) {
    return {
      username: student.studentId,
      user_id: student.studentId,
      first_name: student.firstName,
      last_name: student.lastName,
      name: `${student.firstName} ${student.lastName}`,
      role: "student",
      school: { school_name: currentClass.schoolName },
      timeOffset: 0,
    };
  }

  /**
   * Delete a tournament
   * @param {string} tournamentId - Tournament ID to delete
   */
  static async deleteTournament(tournamentId) {
    return await tournamentManager.deleteTournament(tournamentId);
  }

  /**
   * Add players to tournament synchronously
   * @param {Object} options - Configuration options
   * @param {string} options.tournamentId - Tournament ID
   * @param {Object} options.currentClass - Current class object
   * @param {Array} options.participatingStudentIds - Array of student IDs who should participate (optional)
   */
  static async addPlayersToTournament({
    tournamentId,
    currentClass,
    participatingStudentIds = null,
  }) {
    // Transform students to player data
    const playerDataList = (currentClass.students || []).map((student) =>
      ClassTournamentService.transformStudentToPlayer(student, currentClass)
    );

    // Add all players in a single batch operation with participation logic built-in
    await tournamentManager.addPlayers(
      tournamentId,
      playerDataList,
      participatingStudentIds
    );
  }

  /**
   * Add players to tournament asynchronously via Firebase function
   * @param {Object} options - Configuration options
   * @param {string} options.tournamentId - Tournament ID
   * @param {Object} options.currentClass - Current class object
   * @param {Array} options.participatingStudentIds - Array of student IDs who should participate (optional)
   */
  static async addPlayersToTournamentAsync({
    tournamentId,
    currentClass,
    participatingStudentIds = null,
  }) {
    // Call Firebase function to add players in the background
    const result = await addPlayersToTournamentFunction({
      tournamentId,
      schoolName: currentClass.schoolName,
      students: currentClass.students || [],
      participatingStudentIds,
    });

    return result.data;
  }

  /**
   * Get tournament manager instance
   * @returns {Object} - Tournament manager
   */
  static getTournamentManager() {
    return tournamentManager;
  }
}

export default ClassTournamentService;
